import React from 'react';
import { TableCell, TableRow } from 'src/components/ui/table';
import { Invoice, SKU } from '../../../../redux/billing/billing.types';
import { AtSign, CheckCheck, Contact, Eye, EyeOff, Flag, Hash, Plus, ReceiptText, RefreshCcw, RotateCcw } from 'lucide-react';
import { InvoiceDetailButton } from '../invoice-detail-button';
import { Button } from 'src/components/ui/button';
import { InvoiceDetailCalc } from './invoice-detail-calc';
import { useCreateInvoiceLineItemMutation, useSyncInvoiceMutation } from 'src/redux/billing/billing.endpoints';
import { Company } from 'src/pages/billing/types/billing.types';

interface InvoiceDetailsCardProps {
  invoice: Invoice;
  invoiceIndex: number;
  companyLookup: Record<string, Company>;
  skus: SKU[];
  sortedCompanyNames: Array<{ id: string; name: string }>;
  sortedTerms: Array<{ id: string; name: string }>;
  updateInvoiceField: (invoiceId: string, field: string, value: any) => void;
  refetchInvoiceGroupDetails: () => void;
}

export const InvoiceDetailsCard: React.FC<InvoiceDetailsCardProps> = ({
  invoice,
  skus,
  invoiceIndex,
  companyLookup,
  sortedCompanyNames,
  sortedTerms,
  updateInvoiceField,
  refetchInvoiceGroupDetails,
}) => {
  const [createLineItem, { isLoading: isCreatingLineItem }] = useCreateInvoiceLineItemMutation();
  const [syncInvoice, { isLoading: isSyncingInvoice }] = useSyncInvoiceMutation();

  if (!invoice.company) return null;
  const company = sortedCompanyNames.find(company => company.id === invoice.company);

  const handleCreateLineItem = async() => {
    try {
      await createLineItem({
        name: `user_created_line_item ${company?.name} ${invoice.ext_invoice_number}`,
        invoice_group: invoice.invoice_group,
        invoice: invoice.id,
        attention_needed: true,
      }).unwrap();
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to create line item:', error);
    }
  };

  const handleSyncInvoice = async() => {
    try {
      await syncInvoice({ id: invoice.id }).unwrap();
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to sync invoice:', error);
    }
  };

  return (
    <TableRow
      className={`border-yellow-500 bg-gray-200 border-l-4 border-r-4 border-b-gray-201 border-b-1`}
    >
      <TableCell
        colSpan={6}
        className="px-0 py-0"
      >
        <div className="">
          <div className='flex flex-row border-b border-t border-gray-201 justify-between'>
            <div className='flex flex-row gap-2 items-center ml-2'>
              <InvoiceDetailCalc
                invoice={invoice}
                skus={skus}
              />
            </div>
            <div className='flex flex-row'>
              <Button
                size='med'
                variant='ghost'
                onClick={() => updateInvoiceField(invoice.id, 'hide', !invoice.hide)}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-l border-r border-gray-201'
              >
                {invoice.hide ? <Eye className='h-4 w-4' /> : <EyeOff className='h-4 w-4' />}
                {/* <RefreshCw className="h-4 w-4" /> */}
                {invoice.hide ? 'Show' : 'Hide'}
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={() => updateInvoiceField(invoice.id, 'reset', true)}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201'
              >
                <RotateCcw className='h-4 w-4' />
                Reset
              </Button>
              {/* <Button
                size='med'
                variant='ghost'
                onClick={(() => {})}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201'
              >
                <Split className='h-4 w-4' />
                Split
              </Button> */}
              {/* <Button
                size='med'
                variant='ghost'
                onClick={(() => updateInvoiceField(invoice.id, 'do_not_bill', !invoice.do_not_bill))}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201 ${invoice.do_not_bill ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-300 hover:border-orange-300' : 'bg-transparent'}`}
              >
                <CircleOff className='h-4 w-4' />
                Do Not Bill
              </Button> */}
              <Button
                size='med'
                variant='ghost'
                onClick={(() => updateInvoiceField(invoice.id, 'invoice_status', invoice.invoice_status === 'approved' ? 'draft' : 'approved'))}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201 ${invoice.invoice_status === 'approved' ? 'bg-green-501 text-white border-green-501 hover:bg-green-500 hover:border-green-500' : 'bg-transparent'}`}
              >
                <CheckCheck className='h-4 w-4' />
                Approve
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => updateInvoiceField(invoice.id, 'attention_needed', !invoice.attention_needed))}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201 ${invoice.attention_needed ? 'bg-red-501 text-white border-red-501 hover:bg-red-500 hover:border-red-500' : 'bg-transparent'}`}
              >
                <Flag className='h-4 w-4' />
                Flag
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => handleCreateLineItem())}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201'
              >
                <Plus className='h-4 w-4' />
                Create
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => handleSyncInvoice())}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1'
              >
                <RefreshCcw className={`h-4 w-4 ${isSyncingInvoice ? 'animate-spin' : ''}`} />
                Sync
              </Button>
            </div>


          </div>
          <div className="grid grid-cols-8 gap-3">
            <div className='col-span-2 p-2'>
              <div className='text-black font-bold flex text-md items-center gap-2 justify-between'>
                <div className='flex items-center gap-1'>
                  <Hash className='h-4 w-4 ' />
                  {invoice.ext_invoice_number || 'Not Assigned'}
                </div>
                {/* <StatusBadgeThin status={invoice.invoice_status} /> */}
              </div>
              <div className='text-xs font-light text-gray-500 flex items-center gap-1 ml-1'>
                <div className='w-2 h-2 bg-green-501 rounded-full' />
                {/* TODO replace with verified when implemented */}
                Verified
              </div>
            </div>
            <div className='col-span-6 grid grid-cols-6'>
              <div className='col-span-2' />

              <InvoiceDetailButton
                icon={<Contact className='h-5 w-5 text-white bg-blue-500 rounded-full p-1' />}
                label="Bill To"
                value={companyLookup[invoice.company]?.company_name || ''}
                options={sortedCompanyNames}
                onSelect={(e) => updateInvoiceField(invoice.id, 'company', e)}
                onClick={() => { }}
                isDropdown={true}
                color={'bg-blue-500'}
                useSearch={true}
              >
                <div className='flex flex-row gap-1 items-baseline pt-1 pl-1'>
                  {companyLookup[invoice.company]?.emails?.split(',').map(email => (
                    <div
                      key={email}
                      className='text-[10px] font-light p-0.5 flex items-center text-white'
                    >
                      <AtSign className='h-2 w-2 mr-0.5' />
                      {email?.split('@')[0]}
                    </div>
                  ))}
                </div>
              </InvoiceDetailButton>

              <InvoiceDetailButton
                icon={<ReceiptText className='h-5 w-5 text-white bg-yellow-500 rounded-full p-1' />}
                label="Terms"
                value={sortedTerms.find(term => term.id === invoice.terms)?.name || ''}
                options={sortedTerms}
                onSelect={(e) => updateInvoiceField(invoice.id, 'terms', e)}
                onClick={() => { } }
                isDropdown={true}
                color={'bg-yellow-500'}
              />

              {/* <div className='col-span-4 grid grid-cols-5'>

                <div
                  onClick={() => updateInvoiceField(invoice.id, 'hide', !invoice.hide)}
                  className='col-span-1 flex flex-col gap-1 items-center justify-center h-full cursor-pointer hover:bg-gray-201'
                >
                  {invoice.hide ? <Eye className='h-8 w-8 text-black p-1' /> : <EyeOff className='h-8 w-8 text-black p-1' />}
                  <div className='text-normal font-bold text-black cursor-pointer'>
                    {invoice.hide ? 'Show' : 'Hide'}
                  </div>
                </div>

                <div className=' col-span-1 flex flex-col gap-1 items-center justify-center h-full cursor-pointer hover:bg-gray-201'>
                  <CircleOff className='h-8 w-8 text-black p-1' />
                  <div className='text-normal font-bold text-black cursor-pointer text-center'>
                    Do Not Bill
                  </div>
                </div>
                <div className=' col-span-1 flex flex-col gap-1 items-center justify-center h-full cursor-pointer hover:bg-gray-201'>
                  <CheckCheck className='h-8 w-8 text-black p-1' />
                  <div className='text-normal font-bold text-black cursor-pointer text-center'>
                    Approve
                  </div>
                </div>
                <div className={`col-span-1 flex text-black flex-col gap-1 items-center justify-center h-full cursor-pointer hover:bg-gray-201 hover:text-black ${invoice.attention_needed ? 'bg-red-501 text-white' : ''}`}>
                  <Flag className='h-8 w-8 p-1' />
                  <div className='text-normal font-bold cursor-pointer text-center'>
                    Attention Needed
                  </div>
                </div>
                <div className=' col-span-1 flex flex-col gap-1 items-center justify-center h-full cursor-pointer hover:bg-gray-201'>
                  <RefreshCcw className='h-8 w-8 text-black p-1' />
                  <div className='text-normal font-bold text-black cursor-pointer text-center'>
                    Sync
                  </div>
                </div>
              </div> */}

            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default InvoiceDetailsCard;
